/**
 * Query keys, in one place.
 *
 * Every cache read, write, invalidation and the persistence filter derive their key
 * from here — so changing the shape is a single edit, not a hunt.
 */
import type { Lang } from '@framework/core';

export const SECTION_KEY_ROOT = 'section';
export const HISTORY_KEY_ROOT = 'history';

/**
 * localStorage key the persisted section cache lives under.
 *
 * Here rather than in the app's entry point because two places need to agree on it:
 * whoever configures the persister, and flushLocal(), which deletes it. A local
 * flush that missed this key would be undone by the next reload.
 */
export const PERSISTED_CACHE_KEY = 'cms.sections';

/**
 * The server's cache epoch, as this browser last saw it.
 *
 * PERSISTED, and that is the whole point. The epoch lived in a module variable, so a
 * freshly loaded page had never "seen" one and recorded whatever arrived as the truth —
 * which meant a client could not tell a deploy had happened, and kept a document whose
 * SHAPE was out of date. The version number cannot catch that: the stored row has not
 * changed, so the server rightly answers CURRENT and sends nothing, and a field added to
 * the section since is missing from what the page renders until somebody saves it.
 */
export const EPOCH_KEY = 'cms.epoch';

/** Keyed by (name, lang) only. Identity is NOT part of the key — see capability-store. */
export const sectionKey = (name: string, lang: Lang) => [SECTION_KEY_ROOT, name, lang] as const;

export const historyKey = (name: string, lang: Lang) => [HISTORY_KEY_ROOT, name, lang] as const;

export const sessionKey = ['session'] as const;
export const profileKey = ['profile'] as const;
export const adminUsersKey = ['admin', 'users'] as const;

/** Only sections are written to localStorage — never sessions or permissions. */
export const isPersistableKey = (key: readonly unknown[]): boolean =>
  key[0] === SECTION_KEY_ROOT;

/**
 * A denial must NEVER be persisted.
 *
 * `denied` records a transient answer — the server was unreachable, or the session
 * had no access at that moment. Writing it to localStorage makes it survive for the
 * cache lifetime, and <SectionUpdate> renders nothing when denied. The result is a
 * site that looks completely empty long after the original problem is fixed, with no
 * error anywhere to explain it. That happened.
 */
export const isPersistableEntry = (data: unknown): boolean =>
  !(typeof data === 'object' && data !== null && 'denied' in data && Boolean((data as { denied?: unknown }).denied));
